import { ClientSearch } from "@core/components/client-search";
import { Client, NoteListItem } from "creso-core/types";
import { useNotes } from "creso-core/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2Icon, NotebookPen } from "lucide-react";
import { NoteListItemComponent } from "./note-list-item";
import { useNotebookContext } from "./notebook-context";
import { useFamilyMemberNotes } from "./use-family-member-notes";

export function NotesList() {
  const { selectedNote, filters, setFilters, reset, clientIds } =
    useNotebookContext();

  const [transientNote, setTransientNote] = useState<NoteListItem | null>();

  const [selectedClient, setSelectedClient] = useState<Client | null>(
    filters.client
  );

  useEffect(() => setSelectedClient(filters.client), [filters.client]);

  const isFamilyMode = Boolean(clientIds?.length);

  const { notes: familyNotes, isLoading: isFamilyNotesLoading } =
    useFamilyMemberNotes(isFamilyMode ? (clientIds ?? []) : []);

  const {
    notes: clientNotes,
    fetchNextPage: fetchNextNotes,
    hasNextPage: hasMoreNotes,
    isFetchingNextPage: isFetchingMoreNotes,
    isLoading: isClientNotesLoading,
  } = useNotes(
    !isFamilyMode && selectedClient
      ? { clientId: selectedClient.id }
      : undefined,
    { enabled: !isFamilyMode }
  );

  const notes = useMemo(() => {
    if (!isFamilyMode) return clientNotes ?? [];
    if (selectedClient) {
      return familyNotes.filter((note) => note.client_id === selectedClient.id);
    }
    return familyNotes;
  }, [isFamilyMode, clientNotes, familyNotes, selectedClient]);

  const isLoading = isFamilyMode ? isFamilyNotesLoading : isClientNotesLoading;

  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (!isLoading) {
      if (selectedNote) {
        const note = notes.find((n) => n.id === selectedNote.id);
        if (!note) {
          setTransientNote(selectedNote);
        } else {
          setTransientNote(null);
        }
      } else if (notes.length) {
        setTransientNote(null);
        resetRef.current(notes[0]);
      }
    }
  }, [notes, selectedNote, isLoading]);

  const selectedNoteRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedNoteRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedNote?.id]);

  const notesBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!notesBottomRef.current || !hasMoreNotes || isFamilyMode) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextNotes();
      },
      { threshold: 0.1 }
    );
    observer.observe(notesBottomRef.current);
    // eslint-disable-next-line
    return () => observer.disconnect();
  }, [hasMoreNotes, fetchNextNotes, isFamilyMode]);

  return (
    <div className="flex flex-col flex-1 gap-4">
      <ClientSearch
        client={selectedClient}
        clientIds={clientIds}
        placeholder={clientIds ? "Select member" : "Select client"}
        onSelect={(client) => {
          setSelectedClient(client ?? null);
          setFilters((f) => ({ ...f, client: client ?? null }));
          if (!client) {
            reset(client, true);
          }
        }}
        options={{ modal: true }}
      />

      <div className="flex flex-1 flex-col">
        {transientNote ? (
          <NoteListItemComponent
            key={transientNote?.id}
            ref={selectedNoteRef}
            isSelected
            onSelect={() => reset(transientNote)}
            isNextSelected={false}
            note={transientNote}
          />
        ) : null}
        {notes.map((note, idx) => {
          const next = notes[idx + 1];
          const isNextSelected = next?.id === selectedNote?.id;
          const isSelected = selectedNote?.id === note?.id;
          return (
            <NoteListItemComponent
              key={note?.id}
              ref={isSelected ? selectedNoteRef : undefined}
              isSelected={isSelected}
              onSelect={() => reset(note)}
              isNextSelected={isNextSelected}
              note={note}
            />
          );
        })}
        {notes.length === 0 && (
          <div className="flex flex-col gap-4 flex-1 items-center justify-center">
            <NotebookPen className="size-12 stroke-1 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center">
              No notes yet
            </p>
          </div>
        )}
      </div>
      <div ref={notesBottomRef} className="h-px" />
      {isFetchingMoreNotes && (
        <div className="flex justify-center py-2">
          <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
